import { MOCKED_NFT_INITIAL_STATE } from 'e2e-tests/mocks/wagmi.mocks';
import { PublicClient, WalletClient } from 'viem';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { IQuest, UserQuestStruct } from 'modules/daily-quests-page/daily-quests-page.types';
import { ORDER, TIER_ID, TierKey } from 'modules/nfts/constants';
import { Tiers } from 'modules/nfts/constants/types';
import { readGetUserInfo } from 'modules/nfts/functions/read-functions';
import { writeForge, writeMint } from 'modules/nfts/functions/write-functions';

import { MovesEnum } from 'shared/constants/shared-enums';
import { isMocked } from 'shared/utils/isMocked';

const MOVES = [
  { key: 'rock' as const, enumKey: MovesEnum.Rock },
  { key: 'paper' as const, enumKey: MovesEnum.Paper },
  { key: 'scissors' as const, enumKey: MovesEnum.Scissors },
];

/** Converts readGetUserInfo(...)[4] (21-item bigint[]) → TierCounts */
export function mapArrayToTierCounts(arr?: readonly bigint[]): TierCounts {
  const res = {} as TierCounts;
  if (!arr) return res;

  for (const tierKey of ORDER) {
    for (const m of MOVES) {
      const id = TIER_ID[tierKey][m.enumKey];
      res[id] = arr[id - 1] ?? 0n;
    }
  }

  return res;
}

/** Build full nested balances and set canMint/Forge flags */
export function buildNftsCountAll(balances: TierCounts, pointBalance: bigint): NftBalances {
  const res = {} as NftBalances;
  let timeOut = 0;
  for (let i = 0; i < ORDER.length; i++) {
    const col = ORDER[i];
    res[col] = {} as Record<Exclude<MovesEnum, MovesEnum.None>, Cell>;

    for (const m of MOVES) {
      const id = TIER_ID[col][m.enumKey];
      const count = Number(balances[id] ?? 0n);
      const prevId = id - 3 > 0 ? id - 3 : null;
      const prevCount = prevId ? Number(balances[prevId] ?? 0n) : 0;
      if (count > 0) {
        timeOut += 200;
      }
      res[col][m.enumKey] = {
        count,
        canForge: prevCount >= 1,
        canMint: id < 4 && Number(pointBalance) >= 100,
        openTimeOut: count > 0 ? timeOut : null,
      };
    }
  }

  return res;
}

/** Highest owned tier per move (grandmaster → bronze) */
export function computeBestTierPerMove(
  balances: TierCounts,
): Record<Exclude<MovesEnum, MovesEnum.None>, Tiers> {
  const result = {} as Record<Exclude<MovesEnum, MovesEnum.None>, Tiers>;

  for (const m of MOVES) {
    for (let i = ORDER.length - 1; i >= 0; i--) {
      const id = TIER_ID[ORDER[i]][m.enumKey];
      if ((balances[id] ?? 0n) > 0n) {
        result[m.enumKey] = id;
        break;
      }
    }
  }

  return result;
}

/* ---------------- NFT types ---------------- */
type Cell = { count: number; canForge: boolean; canMint: boolean; openTimeOut: number | null };
export type NftBalances = Record<TierKey, Record<Exclude<MovesEnum, MovesEnum.None>, Cell>>;
type TierCounts = Partial<Record<Tiers, bigint>>;

/* ---------------- Quests + NFT state ---------------- */
export interface QuestsStore {
  /* NFT part */
  nftBalances: NftBalances | null;
  questsData: UserQuestStruct | null;
  pointBalance: bigint;
  bestTierPerMove: Record<Exclude<MovesEnum, MovesEnum.None>, Tiers> | null;
  bestOpponentTierPerMove: Record<Exclude<MovesEnum, MovesEnum.None>, Tiers> | null;
  snapshotPoints: bigint;
  isMinting: boolean;
  minted: boolean;
  isForging: boolean;
  isForgeSuccess: boolean | null;
  mintError: boolean | null;
  forgeError: boolean | null;

  /* Quests part */
  items: IQuest[];
  activeQuests: number;
  totals: {
    claimable: number;
    txPending: string;
    bonusPoints: number;
  };
  loading: boolean;
  error: string | null;
  claim: (id: string) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export interface QuestsActions {
  /* NFT actions */
  setSnapshotPoints: (points?: bigint) => void;
  fetchUserBalances: (
    provider: PublicClient,
    account: `0x${string}`,
    shouldSetSnapshotPoints?: boolean,
  ) => Promise<void>;
  setNFTBalance: (nftBalance: NftBalances | null) => void;
  setQuestsData: (questsData: UserQuestStruct | null) => void;
  setTopHands: (
    bestTierPerMove: Record<MovesEnum.Rock | MovesEnum.Scissors | MovesEnum.Paper, Tiers>,
  ) => void;
  fetchOpponentTopTiers: (provider: PublicClient, account: `0x${string}`) => Promise<void>;
  clearOpponentTopTiers: () => void;
  mintNft: (
    provider: PublicClient,
    walletClient: WalletClient,
    account: `0x${string}`,
    move: MovesEnum,
    amount: number,
  ) => Promise<void>;
  forgeNft: (
    provider: PublicClient,
    walletClient: WalletClient,
    account: `0x${string}`,
    tier: Tiers,
    amount: number,
    move: MovesEnum,
    collection: TierKey,
    prevCollection: TierKey,
  ) => Promise<void>;
  setPoints: (points: bigint) => void;
  setMinted: (minted: boolean) => void;
  setMinting: (minting: boolean) => void;
  setForging: (forging: boolean) => void;
  setForgeSuccess: (forgeSuccess: boolean) => void;
  setMintError: (mintError: boolean | null) => void;
  setForgeError: (forgeError: boolean | null) => void;

  /* Quests actions */
  setItems: (items: IQuest[]) => void;
  setActiveQuests: (count: number) => void;
  setTotals: (totals: QuestsStore['totals']) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setClaim: (fn: QuestsStore['claim']) => void;
  setRefetch: (fn: QuestsStore['refetch']) => void;
}

/* ---------------- Initial state ---------------- */
export const initialState: QuestsStore = {
  /* NFT */
  nftBalances: null,
  questsData: null,
  pointBalance: 0n,
  bestTierPerMove: null,
  bestOpponentTierPerMove: null,
  snapshotPoints: 0n,
  isMinting: false,
  minted: false,
  isForging: false,
  isForgeSuccess: null,
  mintError: null,
  forgeError: null,

  /* Quests */
  items: [],
  activeQuests: 0,
  totals: { claimable: 0, txPending: '', bonusPoints: 0 },
  loading: true,
  error: null,
  claim: async () => false,
  refetch: async () => {},
};

/* ---------------- Store ---------------- */
export const useQuestsStore = create<QuestsStore & QuestsActions>()(
  immer((set, get) => ({
    ...initialState,

    setItems: (items) =>
      set((s) => {
        s.items = [...items];
      }),

    setActiveQuests: (count) =>
      set((s) => {
        s.activeQuests = count;
      }),

    setTotals: (totals) =>
      set((s) => {
        s.totals = { ...totals };
      }),

    setLoading: (loading) =>
      set((s) => {
        s.loading = loading;
      }),

    setError: (error) =>
      set((s) => {
        s.error = error;
      }),

    setClaim: (claim) =>
      set((s) => {
        s.claim = claim;
      }),

    setRefetch: (refetch) =>
      set((s) => {
        s.refetch = refetch;
      }),

    fetchUserBalances: async (provider, account, shouldSetSnapshotPoints = false) => {
      if (isMocked) {
        set(MOCKED_NFT_INITIAL_STATE);
        return;
      }
      if (!provider || !account) return;

      const balance = await readGetUserInfo(provider, account);

      const points = (balance[2] as bigint) ?? 0n;
      const arr = balance[4] as readonly bigint[];
      const tierCounts = mapArrayToTierCounts(arr);

      const mapped = buildNftsCountAll(tierCounts, balance[2] as bigint);
      const bestTierPerMove = computeBestTierPerMove(tierCounts);
      set({
        pointBalance: points,
        questsData: balance[1] as UserQuestStruct,
        nftBalances: mapped,
        bestTierPerMove,
        ...(shouldSetSnapshotPoints ? { snapshotPoints: points } : {}),
      });
    },

    fetchOpponentTopTiers: async (provider, account) => {
      if (isMocked) {
        set(MOCKED_NFT_INITIAL_STATE);
        return;
      }
      const balance = await readGetUserInfo(provider, account);

      const arr = balance[4] as readonly bigint[];
      const tierCounts = mapArrayToTierCounts(arr);

      const bestOpponentTierPerMove = computeBestTierPerMove(tierCounts);
      set({ bestOpponentTierPerMove });
    },

    clearOpponentTopTiers: () => {
      set({
        bestOpponentTierPerMove: null,
      });
    },

    setNFTBalance: (nftBalances: NftBalances | null) =>
      set({
        nftBalances,
      }),

    setQuestsData: (questsData: UserQuestStruct | null) =>
      set({
        questsData,
      }),

    setTopHands: (
      bestTierPerMove: Record<MovesEnum.Rock | MovesEnum.Scissors | MovesEnum.Paper, Tiers>,
    ) =>
      set({
        bestTierPerMove,
      }),

    setPoints: (points: bigint) =>
      set({
        pointBalance: points,
      }),

    setSnapshotPoints: (points?: bigint) =>
      set((state) => ({
        snapshotPoints: points ?? state.pointBalance,
      })),

    mintNft: async (provider, walletClient, account, move, amount) => {
      // Bronze mint mapping
      const tierMap: Record<Exclude<MovesEnum, MovesEnum.None>, Tiers> = {
        [MovesEnum.Rock]: Tiers.BronzeRock,
        [MovesEnum.Paper]: Tiers.BronzePaper,
        [MovesEnum.Scissors]: Tiers.BronzeScissors,
      };
      try {
        set({ isMinting: true, minted: false });
        const tier = tierMap[move];
        const txHash = await writeMint(provider, walletClient, tier, amount, account);
        await provider.waitForTransactionReceipt({ hash: txHash });
        // small delay to ensure blockchain state is updated before fetching balances
        await new Promise((r) => setTimeout(r, 4000));
        await get().fetchUserBalances(provider, account);
      } catch (error) {
        console.error('Error in mintNft:', error);
        set({ mintError: true });
      } finally {
        let error = get().mintError;
        set({ isMinting: false, minted: error ? false : true });
      }
    },

    forgeNft: async (
      provider,
      walletClient,
      account,
      tier,
      amount,
      move,
      collection,
      prevCollection,
    ) => {
      let isForgeSuccess = null;
      try {
        set({ isForging: true });
        const txHash = await writeForge(provider, walletClient, tier, amount, account);
        let prevColeectionNFTsCount = get().nftBalances?.[prevCollection]?.[move]?.count;
        let forgableCollectionNFTsCount = get().nftBalances?.[collection]?.[move]?.count;
        await provider.waitForTransactionReceipt({ hash: txHash });
        await new Promise((r) => setTimeout(r, 4000));
        await get().fetchUserBalances(provider, account);
        let newPrevCollectionNFTsCount = get().nftBalances?.[prevCollection]?.[move]?.count;
        let newForgableCollectionNFTsCount = get().nftBalances?.[collection]?.[move]?.count;
        isForgeSuccess =
          prevColeectionNFTsCount === newPrevCollectionNFTsCount + amount &&
          forgableCollectionNFTsCount + 1 === newForgableCollectionNFTsCount;
      } catch (error) {
        console.error('Error in forgeNft:', error);
        set({ forgeError: true });
      } finally {
        set({ isForging: false, isForgeSuccess });
      }
    },
    setMinted: (minted) => set({ minted }),
    setMinting: (minting) => set({ isMinting: minting }),
    setForging: (forging) => set({ isForging: forging }),
    setForgeSuccess: (forgeSuccess) => set({ isForgeSuccess: forgeSuccess }),
    setMintError: (mintError) => set({ mintError }),
    setForgeError: (forgeError) => set({ forgeError }),
  })),
);
